import React, { ReactElement } from "react";
import {
    createListTableColumns,
    ensure,
    ListTableColumn,
    renderOptionalName,
    Stack,
    TableColumn
} from "sprint-common-web";

import { DynamicSubtitleType } from "../../../common/api/master-api/assets/search/common/SearchAssetsSubtitleResponse";
import { LanguageDetailsInfo } from "../../../common/components/details-info/LanguageDetailsInfo";
import { VideoDetailsInfo } from "../../../common/components/details-info/VideoDetailsInfo";
import { renderTrailerInfo } from "../../../common/components/trailer-info/renderTrailerInfo";
import { ensureAncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import { AssetType } from "../../../common/types/AssetType";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { ensureMovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { RecordType } from "../../../common/types/RecordType";
import { renderAssetInfo } from "../../../common/utils/renderAssetInfo";
import { renderAssetStatus } from "../../../common/utils/renderAssetStatus";
import { renderAssetType } from "../../../common/utils/renderAssetType";
import { renderContent } from "../../../content-ingestion/file-records/common/components/renderContent";
import {
    getCloudAncillaryDetailsHref,
    getCloudMovieDetailsHref,
    getCloudTvSeriesDetailsHref,
    getOnPremAncillaryDetailsHref,
    getOnPremMovieDetailsHref,
    getOnPremTvSeriesDetailsHref
} from "../../AssetsRoutes";
import { renderAssetOrigin } from "../../common/utils/renderAssetOrigin";
import { AssetItem } from "./AssetItem";
import { AssetColumn, AssetListingConfig, sortColumnsByConfiguredOrder } from "./config/AssetListingConfig";
import { renderCreated, renderUpdated } from "./renderAudits";

type CreateAssetListingColumnsProps = {
    config: AssetListingConfig;
};

type AssetListingColumn = TableColumn<AssetItem>;

export const createAssetListTableColumns = ({
    config
}: CreateAssetListingColumnsProps): ListTableColumn<AssetItem>[] => {
    const columns = getListingColumns(config);

    return createListTableColumns<AssetItem>(columns);
};

const getListingColumns = (config: AssetListingConfig): AssetListingColumn[] => {
    const mapping: Record<AssetColumn, AssetListingColumn | undefined> = {
        [AssetColumn.Filepath]: {
            type: "link",
            key: "path",
            name: "File path",
            minWidth: 200,
            maxWidth: 250,
            isResizable: true,
            isSortable: false,
            getHref: (item) => resolveHref(item),
            getTooltipText: (item) => item.path
        },
        [AssetColumn.RecordType]: {
            type: "basic",
            key: "recordType",
            name: "Record type",
            minWidth: 100,
            maxWidth: 100,
            isSortable: false,
            onRender: (item) =>
                renderAssetInfo({
                    recordType: item?.recordType,
                    isTrimmed: !!item?.trimming,
                    testId: "asset-record-type"
                })
        },
        [AssetColumn.Info]: {
            type: "basic",
            key: "isTrailer",
            name: "Info",
            minWidth: 60,
            maxWidth: 80,
            onRender: renderTrailerInfo
        },
        [AssetColumn.Content]: {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 65,
            maxWidth: 350,
            isSortable: true,
            onRender: (item) =>
                renderContent({ content: item?.content, versions: item?.versions, testId: "asset-content" })
        },
        [AssetColumn.AssetType]: {
            type: "basic",
            key: "assetType",
            name: "Asset type",
            minWidth: 90,
            maxWidth: 90,
            isSortable: false,
            onRender: (item) => renderAssetType({ assetType: item?.assetType, testId: "asset-type" })
        },
        [AssetColumn.Originator]: {
            type: "basic",
            key: "originator",
            name: "Originator",
            minWidth: 90,
            maxWidth: 120,
            isSortable: false,
            onRender: (item, index, column) => (
                <Stack data-testId="asset-originator">{renderOptionalName(item, index, column)}</Stack>
            )
        },
        [AssetColumn.Details]: {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 100,
            maxWidth: 120,
            isSortable: false,
            onRender: renderAssetDetails
        },
        [AssetColumn.Origin]: {
            type: "basic",
            key: "origin",
            name: "Source",
            minWidth: 60,
            maxWidth: 60,
            isSortable: false,
            onRender: (item, index, column) => (
                <Stack data-testId="asset-origin">{renderAssetOrigin(item, index, column)}</Stack>
            )
        },

        [AssetColumn.Status]: {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 70,
            maxWidth: 70,
            isSortable: false,
            onRender: (item, index, column) => (
                <Stack data-testId="asset-status">{renderAssetStatus(item, index, column)}</Stack>
            )
        },
        [AssetColumn.Updated]: {
            type: "basic",
            key: "updatedAudit",
            name: "Updated",
            minWidth: 180,
            maxWidth: 200,
            isResizable: true,
            isSortable: true,
            onRender: renderUpdated
        },
        [AssetColumn.Created]: {
            type: "basic",
            key: "createdAudit",
            name: "Created",
            minWidth: 180,
            maxWidth: 200,
            isResizable: true,
            isSortable: true,
            onRender: renderCreated
        }
    };

    return sortColumnsByConfiguredOrder(config.columns)
        .filter(([_, column]) => column.visible)
        .map(([key]) => mapping[Number(key) as AssetColumn])
        .filter((column): column is AssetListingColumn => column !== undefined);
};

const resolveHref = (item: AssetItem): string => {
    return item.assetType === AssetType.Cloud ? resolveCloudHref(item) : resolveOnPremHref(item);
};

const resolveOnPremHref = (item: AssetItem): string => {
    switch (item.contentType) {
        case ContentReferenceType.Movie:
            ensureMovieContentSubtype(item.contentSubtype);
            return getOnPremMovieDetailsHref(item.id, item.contentSubtype);
        case ContentReferenceType.TvSeries:
            return getOnPremTvSeriesDetailsHref(item.id);
        case ContentReferenceType.Ancillary:
            ensureAncillaryContentSubtype(item.contentSubtype);
            return getOnPremAncillaryDetailsHref(item.id, item.contentSubtype);
        default:
            return "";
    }
};

const resolveCloudHref = (item: AssetItem): string => {
    switch (item.contentType) {
        case ContentReferenceType.Movie:
            ensureMovieContentSubtype(item.contentSubtype);
            return getCloudMovieDetailsHref(item.id, item.contentSubtype);
        case ContentReferenceType.TvSeries:
            return getCloudTvSeriesDetailsHref(item.id);
        case ContentReferenceType.Ancillary:
            ensureAncillaryContentSubtype(item.contentSubtype);
            return getCloudAncillaryDetailsHref(item.id, item.contentSubtype);
        default:
            return "";
    }
};

export const renderAssetDetails = (item?: AssetItem): ReactElement | null => {
    ensure(item);

    switch (item.details.type) {
        case RecordType.Video:
            return (
                <VideoDetailsInfo
                    key="video"
                    subtitle={item.details.embeddedSubtitle}
                    aspectRatio={item.details.aspectRatio}
                    testId="asset-details"
                />
            );
        case RecordType.Audio:
            return <LanguageDetailsInfo key="audio" language={item.details.soundtrack} testId="asset-details" />;
        case RecordType.Subtitle:
            return (
                <LanguageDetailsInfo
                    key="subtitle"
                    inPreparation={item.details.inPreparation}
                    language={item.details.subtitle}
                    subtitleType={
                        item.details.subtitleType ? DynamicSubtitleType[item.details.subtitleType] : undefined
                    }
                    testId="asset-details"
                />
            );
        default:
            return <></>;
    }
};
